import { ComponentRef, ElementRef, Injectable, Injector } from '@angular/core';
import { ComponentType, ConnectionPositionPair, Overlay, OverlayConfig, OverlayPositionBuilder, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import { PopupTooltipPositions } from '@ui/misc/popup-tooltip/cog/popup-tooltip-positions';
import { ICoordinates } from '@charting/models';

import { PopupTooltipRef } from './cog/popup-tooltip-ref';
import { PopupTooltipConfig } from './cog/popup-tooltip-config';
import { PopupTooltipBaseComponent } from './base/popup-tooltip-base.component';

export class PopupParameters
{
    width: boolean = true;
    HasFooter: boolean = false;
}

@Injectable({
    providedIn: 'root'
})
export class PopupTooltipService<T extends PopupTooltipBaseComponent<P>, P>
{
    /**
     * Référence vers l'overlay qui contient le composant de notre tooltip
     */
    public overlayRef: OverlayRef = null;
    /**
     * La configuration nécessaire à l'affichage
     */
    private config: PopupTooltipConfig;
    /**
     * L'élement du DOM auquel est rattaché la PopupTooltip
     */
    private origin: ElementRef | ICoordinates;
    private injector: Injector;
    private popupTooltipRef: PopupTooltipRef;
    private componentRef: ComponentRef<T>;

    constructor (
        private overlay: Overlay,
        protected overlayPositionBuilder: OverlayPositionBuilder,
    )
    {
    }

    public create (origin: any, component: ComponentType<T>, config: PopupTooltipConfig, parameters: P): T
    {
        if (this.overlayRef)
        {
            this.close();
        }

        this.origin = origin;
        this.config = config;
        this.overlayRef = this.overlay.create(this.getOverlayConfig());
        this.popupTooltipRef = new PopupTooltipRef(this.overlayRef, config);

        this.injector = Injector.create({
            providers: [
                {
                    provide: PopupTooltipRef,
                    useFactory: (): PopupTooltipRef =>
                        this.popupTooltipRef
                }
            ]
        });
        this.componentRef = this.overlayRef.attach(new ComponentPortal(component, null, this.injector));

        // @Input()
        this.componentRef.instance.parameters = parameters;

        return this.componentRef.instance;
    }

    public close ()
    {
        this.overlayRef?.dispose();
    }

    private getOverlayConfig (): OverlayConfig
    {
        return new OverlayConfig({
            hasBackdrop: false,
            backdropClass: 'popup-tooltip-backdrop',
            positionStrategy: this.getOverlayPosition(),
            scrollStrategy: this.overlay.scrollStrategies.close()
        });
    }

    private getOverlayPosition (): PositionStrategy
    {
        return this.overlay
            .position()
            .flexibleConnectedTo(this.origin)
            .withPositions(this.getPositions())
            .withPush(true);
    }

    private getPositions (): ConnectionPositionPair[]
    {
        return this.config.positions ? [...this.config.positions, ...PopupTooltipPositions.defaultPositions] : [...PopupTooltipPositions.defaultPositions];
    }
}
